import { createContext, ReactElement, ReactNode, useContext, useMemo } from 'react'

import { useUserOrganization } from '@modules-deprecated/app/organization'
import { Organization } from '@modules-deprecated/app/organization/types/organization'

import { TableIds } from '../../../../../enums/tableIds'
import { QuotesTableAccessor } from '../enums/quotesTableAccessor'

interface ContextState {
  columns: QuotesTableAccessor[]
  newDocumentUrl: string
  options: {
    isFetchEnabled: boolean
    showPeriodSelector: boolean
    showSearch: boolean
    showSummary: boolean
    showTitle: boolean
    persistFiltersInUrl: boolean
  }
  organization: Organization | undefined
  tableId: TableIds
}

const QuotesListContext = createContext<ContextState | undefined>(undefined)

export interface QuotesListProviderProps {
  children?: ReactNode
  columns?: QuotesTableAccessor[]
  newDocumentUrl: ContextState['newDocumentUrl']
  options: ContextState['options']
  tableId: ContextState['tableId']
}

const defaultColumns: QuotesTableAccessor[] = [
  QuotesTableAccessor.Status,
  QuotesTableAccessor.QuoteNumber,
  QuotesTableAccessor.IssueDate,
  QuotesTableAccessor.DueDate,
  QuotesTableAccessor.ContactId,
  QuotesTableAccessor.Description,
  QuotesTableAccessor.NetTotal,
]

export const QuotesListProvider = ({
  children,
  columns,
  newDocumentUrl,
  options,
  tableId,
}: QuotesListProviderProps): ReactElement => {
  const { organization } = useUserOrganization()

  const value = useMemo(
    () => ({
      columns: columns ?? defaultColumns,
      newDocumentUrl,
      options,
      organization,
      tableId,
    }),
    [columns, newDocumentUrl, options, organization, tableId],
  )

  return <QuotesListContext.Provider value={value}>{children}</QuotesListContext.Provider>
}

export const useQuotesList = () => {
  const context = useContext(QuotesListContext)

  if (!context) {
    throw new Error('QuotesListContext is missing in the module!')
  }

  return context
}
